/**
 * Heading Input Rules Plugin for ProseMirror
 *
 * Provides markdown-style shortcuts for creating headings:
 * - "# " at line start → Heading 1 (h1)
 * - "## " at line start → Heading 2 (h2)
 * - "### " at line start → Heading 3 (h3)
 *
 * Features:
 * - Only activates when editor is editable
 * - Replaces paragraph with heading (uses setBlockType)
 * - Removes the trigger text after conversion
 * - Preserves blockKey attribute through conversion
 * - Works at start of empty or non-empty paragraphs
 */

import type { Plugin } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";
import { inputRules, textblockTypeInputRule } from "@tiptap/pm/inputrules";
import { schema } from "../schema";

export const headingInputRulesPluginKey = new PluginKey("headingInputRules");

/**
 * Creates a plugin that enables markdown-style heading shortcuts.
 *
 * Typing "# ", "## ", or "### " at the start of a line creates
 * a heading of level 1, 2, or 3 respectively.
 *
 * The space after the hash marks is required to prevent false matches
 * (e.g., "#text" won't trigger the rule).
 *
 * @returns Plugin that handles heading input rules
 */
export function createHeadingInputRulesPlugin(): Plugin {
  // Heading 1: "# " at start of line
  // Pattern: ^# $
  // - ^ = start of paragraph
  // - # = single hash
  // - (space) = required space
  // - $ = end of match (required by inputRules API)
  const heading1Rule = textblockTypeInputRule(/^# $/, schema.nodes.heading, {
    level: 1,
  });

  // Heading 2: "## " at start of line
  // Pattern: ^## $
  // - ^ = start of paragraph
  // - ## = two hashes
  // - (space) = required space
  // - $ = end of match (required by inputRules API)
  const heading2Rule = textblockTypeInputRule(/^## $/, schema.nodes.heading, {
    level: 2,
  });

  // Heading 3: "### " at start of line
  // Pattern: ^### $
  // - ^ = start of paragraph
  // - ### = three hashes
  // - (space) = required space
  // - $ = end of match (required by inputRules API)
  const heading3Rule = textblockTypeInputRule(/^### $/, schema.nodes.heading, {
    level: 3,
  });

  return inputRules({
    rules: [heading1Rule, heading2Rule, heading3Rule],
  });
}
